import axios, { AxiosInstance } from "axios";
import { attachSession } from "./session";

/**
 * Shared plumbing for app-backend, the platform's backend: where it is, the bearer token on
 * every request, and the `{code, detail}` envelope on the way out.
 *
 * Platform-level on purpose. Billing is a feature beside the products rather than part of one,
 * so it does not belong in `cohortApi.ts`, which is the SignalScan-ME product's client. The two
 * keep separate axios instances even when they reach the same deployment: a change to either
 * one's interceptors or timeouts then stays out of the other's calls.
 */

/** What every refusal from app-backend carries, whatever the status. */
export interface AppBackendError {
  code: string;
  detail: string;
}

/**
 * The Docker image bakes `IQ_APP_*` placeholders into the bundle and `env.sh` substitutes them
 * at container start. A deployment that never sets the variable therefore leaves the literal
 * placeholder in the built JavaScript, which would otherwise be used as a URL.
 */
const isUnsubstitutedPlaceholder = (value?: string): boolean =>
  !value || value.startsWith("IQ_APP_");

/**
 * Where app-backend is, or `null` when this deployment cannot reach it at all.
 *
 * Unlike the cohort service there is no path on the dashboard's own origin to fall back to:
 * told nothing, a deployment that is not localhost has nowhere to send the call, and the caller
 * hides whatever it was going to show rather than guessing at a URL.
 *
 * Read on each call rather than once at import, so that a caller sees the same answer the
 * environment gives now.
 */
export const appBackendBaseUrl = (): string | null => {
  const configured = process.env.REACT_APP_BACKEND_API_BASE_URL;
  if (!isUnsubstitutedPlaceholder(configured)) {
    return (configured as string).replace(/\/+$/, "");
  }

  const { protocol, hostname } = window.location;
  // Local development runs app-backend from its own docker-compose, which publishes it on 8000.
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `${protocol}//${hostname}:8000`;
  }
  return null;
};

/** False when nothing here can reach app-backend, so its features stay out of the page. */
export const isAppBackendConfigured = (): boolean =>
  appBackendBaseUrl() !== null;

/**
 * An axios instance on app-backend. One per call rather than one per module: the base URL is
 * read at call time, and two instances a page load cost nothing next to the requests themselves.
 *
 * Throws when the deployment has no app-backend. Callers gate on `isAppBackendConfigured()`
 * first; one that does not gets the throw back through `appBackendErrorOf` as `REQUEST_FAILED`.
 */
export const createAppBackendClient = (timeout: number): AxiosInstance => {
  const baseURL = appBackendBaseUrl();
  if (baseURL === null) {
    throw new Error("app-backend has no base URL in this deployment");
  }

  const api = axios.create({
    baseURL,
    headers: { "Content-Type": "application/json" },
    timeout,
  });

  // app-backend verifies this against the realm's JWKS and scopes the wallet it charges to the
  // `sub` inside it, so this header is not decoration: without it every call is a 401.
  // `attachSession` puts it on, renewed when it is about to run out, and holds a call turned
  // away for it until the user has signed in again.
  attachSession(api);

  return api;
};

/**
 * Pull `{code, detail}` out of whatever the transport produced. A failure that never reached the
 * backend — no network, a blocked cross-origin request, a timeout — has no envelope to read, and
 * comes back as `REQUEST_FAILED` with the transport's own message, which is for the log rather
 * than for the user.
 */
export const appBackendErrorOf = (error: unknown): AppBackendError => {
  const response = (
    error as { response?: { data?: { error?: AppBackendError } } }
  )?.response;
  const carried = response?.data?.error;
  if (carried && typeof carried.code === "string") {
    return carried;
  }
  const message = (error as Error)?.message || "the request did not complete";
  return { code: "REQUEST_FAILED", detail: message };
};
